import { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { api, API, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Slider } from "./ui/slider";
import { Loader2, Grid3x3, Move, Plus, Trash2, Save, X, Sun, TriangleAlert, CheckCircle2, Compass } from "lucide-react";
import { PanelInUse } from "./PanelInUse";
import { pdfBgImage, hasPdfBg, hasDrawing, EditorGridBg } from "./RoofPdfView";
import { usePanelSelection, SelectionToolbar, MarqueeRect } from "./usePanelSelection";
import { useEditorViewport, ViewportToolbar, svgPoint } from "./useEditorViewport";
import { useSatOffset, SatAlignControls, SatImage } from "./SatAlign";
import { usePanelString, StringButton, StringDialog, StringPreview, rotateGroup, GroupRotate } from "./PanelString";

// ---- geometria (metri locali) ----
function rectCornersFromCenter(cx, cy, w, h, angDeg) {
  const a = (angDeg * Math.PI) / 180, hw = w / 2, hh = h / 2;
  return [[-hw, -hh], [hw, -hh], [hw, hh], [-hw, hh]].map(([dx, dy]) => ({
    x: cx + dx * Math.cos(a) - dy * Math.sin(a),
    y: cy + dx * Math.sin(a) + dy * Math.cos(a),
  }));
}
function pointInPoly(pt, poly) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].x, yi = poly[i].y, xj = poly[j].x, yj = poly[j].y;
    if ((yi > pt.y) !== (yj > pt.y) && pt.x < ((xj - xi) * (pt.y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

function num(v, d = 0) { return (v == null || isNaN(v)) ? "—" : Number(v).toLocaleString("it-IT", { maximumFractionDigits: d }); }

export default function FlatRoofControls({ lead, roof, moduleInfo, apiKey, onLayout, selfToken, blockLayout = false }) {
  const fl = roof.flat_layout || null;
  const base = selfToken ? `/public/self/${selfToken}` : `/leads/${lead.id}`;
  const [tilt, setTilt] = useState((fl?.params?.tilt_deg === 25 ? 25 : 10));
  const [orient, setOrient] = useState(() => {
    const a = Math.round(fl?.params?.azimuth_deg ?? 180);
    let o = ((a - 180) % 360 + 540) % 360 - 180;  // -180..180 rispetto al Sud
    return Math.max(-90, Math.min(90, o));
  });
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingAz, setEditingAz] = useState(false);
  const azTimer = useRef(null);
  const lastGenRef = useRef(0);
  const genSeqRef = useRef(0);

  const azFromOrient = (o) => ((180 + o) % 360 + 360) % 360;

  const generate = async (opts = {}) => {
    if (blockLayout) { toast.error("Scegli prima un pannello per generare il layout"); return; }
    const azValue = opts.az != null ? opts.az : azFromOrient(orient);
    const seq = ++genSeqRef.current;
    if (!opts.silent) setBusy(true);
    try {
      const { data } = await api.post(`${base}/flat-roof-layout`, { tilt_deg: tilt, azimuth_deg: azValue });
      if (opts.silent && seq !== genSeqRef.current) return; // scarta risposte obsolete durante il drag
      onLayout(data);
      if (!data.compatible) toast.error(data.message || "Modulo non compatibile");
      else if (!opts.silent) toast.success(`Layout PRSG: ${data.summary?.count} pannelli · ${data.summary?.kwp} kWp`);
    } catch (e) { if (!opts.silent) toast.error(formatApiError(e.response?.data?.detail) || "Errore generazione"); }
    finally { if (!opts.silent) setBusy(false); }
  };

  // Cambio ORIENTAMENTO pannelli (° rispetto all'azimut/Sud): ruota le file e rigenera in CONTINUO
  // mentre trascini (throttle ~150ms) invece di aspettare che ti fermi.
  const onOrientChange = (v) => {
    const nv = Array.isArray(v) ? v[0] : v;
    setOrient(nv);
    if (!fl?.panels?.length) return;
    clearTimeout(azTimer.current);
    const run = () => { lastGenRef.current = Date.now(); generate({ az: azFromOrient(nv), silent: true }); };
    const since = Date.now() - lastGenRef.current;
    if (since >= 150) run();
    else azTimer.current = setTimeout(run, 150 - since);
  };

  const mod = fl?.module || moduleInfo || roof.module || {};
  const g = fl?.geometry || {};
  const s = fl?.summary || {};

  return (
    <div className="mt-5 border-t border-border pt-4" data-testid="flat-roof-controls">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-heading font-semibold text-sm flex items-center gap-2"><Grid3x3 className="h-4 w-4 text-primary" strokeWidth={1.5} /> Layout Tetto Piano · sistema TORRI PRSG</h3>
      </div>
      <p className="text-xs text-muted-foreground mb-3">Moduli sempre in <b>landscape</b>. Distanza tra file calcolata con criterio TORRI: <b>21 dicembre, mezzogiorno solare locale</b> (nessun parametro modificabile).</p>

      {/* Controlli inclinazione (azimut: default Sud, modificabile sotto il layout) */}
      <div className="mb-3">
        <Label className="text-xs mb-1.5 block">Inclinazione PRSG</Label>
        <div className="flex gap-2">
          {[10, 25].map((t) => (
            <Button key={t} size="sm" variant={tilt === t ? "default" : "outline"} onClick={() => setTilt(t)} data-testid={`flat-tilt-${t}`}>{t}°</Button>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground mt-2">I pannelli sono disposti di default verso <b>Sud</b>. Per ruotarli usa <b>Orientamento pannelli</b> sotto il layout.</p>
      </div>

      <div className="flex justify-end">
        <Button size="lg" onClick={() => generate()} disabled={busy || blockLayout} data-testid="flat-generate-btn" className="gap-2 shadow-lg">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sun className="h-4 w-4" strokeWidth={1.5} />}
          {fl?.panels?.length ? "Rigenera layout" : "Genera layout PRSG"}
        </Button>
      </div>

      <PanelInUse module={{ ...(fl?.module || {}), ...(moduleInfo || roof.module || {}) }} freeArea={!!selfToken} className="mt-3" />

      {fl && !fl.compatible && (
        <div className="mt-3 rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2.5 text-sm text-destructive flex items-start gap-2" data-testid="flat-incompatible">
          <TriangleAlert className="h-4 w-4 shrink-0 mt-0.5" /> <span>{fl.message}</span>
        </div>
      )}

      {fl && fl.compatible && s?.count != null && (
        <>
          {/* Stato */}
          <div className={`mt-3 rounded-lg px-3 py-2 text-sm flex items-center gap-2 font-medium ${fl.status === "verify" ? "border border-amber-400/50 bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-300" : "border border-emerald-500/50 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300"}`} data-testid="flat-status">
            {fl.status === "verify"
              ? <><TriangleAlert className="h-4 w-4 shrink-0 mt-0.5" /> <span>⚠ Verifica tecnica consigliata — la distanza antiombreggiamento ({num(g.D_mm)} mm) supera il campo standard PRSG (600–1200 mm). Configurazione valida ma da confermare con l'ufficio tecnico.{Number(fl.params?.tilt_deg) === 25 ? " Suggerimento: prova a ridurre la staffa a 10° per rientrare nello standard (di norma entrano più pannelli)." : ""}</span></>
              : <><CheckCircle2 className="h-4 w-4 shrink-0" /> ✓ Configurazione compatibile</>}
          </div>

          {/* Riepilogo */}
          <div className="mt-3 rounded-lg border border-border bg-secondary/20 p-3" data-testid="flat-summary">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
              <Row label="Tipo tetto" value="Piano" />
              <Row label="Sistema" value={fl.system} />
              <Row label="Modello PRSG" value={fl.variant} accent />
              <Row label="Modulo" value={mod.model || "—"} />
              <Row label="Orientamento" value="Landscape" />
              <Row label="Inclinazione" value={`${fl.params?.tilt_deg}°`} />
              <Row label="Azimut campo" value={`${Math.round(fl.params?.azimuth_deg)}°`} />
              <Row label="N° file" value={s.n_rows} />
              <Row label="N° pannelli" value={s.count} accent />
              <Row label="Potenza" value={`${num(s.kwp, 2)} kWp`} accent />
              <Row label="Superficie" value={`${num(s.surface_m2, 1)} m²`} />
              <Row label="Distanza libera D" value={`${num(g.D_mm)} mm`} />
              <Row label="Passo file P" value={`${num(g.P_mm)} mm`} />
              <Row label="Altezza sole α" value={`${num(fl.solar?.alpha_deg, 1)}°`} />
            </div>
            <p className="text-[10px] text-muted-foreground mt-2">Criterio antiombreggiamento: {fl.solar?.criterion}. D = distanza libera tra file (non il passo).</p>
          </div>

          {/* Orientamento pannelli (° rispetto all'azimut/Sud): ruota le file in tempo reale */}
          <div className="mt-3" data-testid="flat-azimuth-editor">
            {!editingAz ? (
              <Button size="sm" variant="outline" onClick={() => setEditingAz(true)} data-testid="flat-edit-azimuth-btn">
                <Compass className="h-4 w-4 mr-1" strokeWidth={1.5} /> Modifica orientamento pannelli (attuale {orient > 0 ? "+" : ""}{orient}°{orient === 0 ? " · Sud" : ""})
              </Button>
            ) : (
              <div className="rounded-lg border border-border bg-secondary/20 p-3">
                <div className="flex items-center justify-between mb-1.5 gap-2 flex-wrap">
                  <Label className="text-xs">Orientamento pannelli: <span className="font-bold text-primary" data-testid="flat-azimuth-value">{orient > 0 ? "+" : ""}{orient}°</span> <span className="text-muted-foreground">rispetto all'azimut (Sud)</span>{busy && <Loader2 className="inline h-3 w-3 ml-1.5 animate-spin" />}</Label>
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="ghost" onClick={() => onOrientChange(0)} data-testid="flat-azimuth-sud">Allinea a Sud (0°)</Button>
                    <Button size="sm" variant="outline" onClick={() => setEditingAz(false)} data-testid="flat-azimuth-close">Chiudi</Button>
                  </div>
                </div>
                <Slider value={[orient]} min={-90} max={90} step={1} onValueChange={onOrientChange} data-testid="flat-azimuth-slider" />
                <p className="text-[10px] text-muted-foreground mt-1.5">Ruota i pannelli: la disposizione e il numero di pannelli si aggiornano in tempo reale mentre trascini.</p>
              </div>
            )}
          </div>

          {/* Vista / editor */}
          {(apiKey || hasDrawing(roof)) && fl.panels?.length > 0 && (
            <div className="mt-3">
              {!editing ? (
                <>
                  <Button size="sm" variant="outline" onClick={() => setEditing(true)} className="mb-3" data-testid="flat-edit-btn"><Move className="h-4 w-4 mr-1" strokeWidth={1.5} /> Modifica (sposta / aggiungi / elimina)</Button>
                  <FlatRoofSvg roof={roof} fl={fl} moduleInfo={moduleInfo} />
                </>
              ) : (
                <FlatRoofSvg roof={roof} fl={fl} moduleInfo={moduleInfo} editable base={base}
                  onSaved={(npl) => { onLayout(npl); setEditing(false); }} onClose={() => setEditing(false)} />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="rounded border border-border bg-background px-2 py-1.5">
      <div className="text-[9px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`font-heading font-bold text-sm ${accent ? "text-primary" : ""}`}>{value}</div>
    </div>
  );
}

// ---- SVG viewer + editor tetto piano ----
function FlatRoofSvg({ roof, fl, moduleInfo, editable, base, onSaved, onClose }) {
  const geo = useMemo(() => {
    const center = roof.center || {};
    const lat0 = center.lat, lng0 = center.lng;
    const mlat = 111320.0, mlng = 111320.0 * Math.cos((lat0 * Math.PI) / 180);
    const toM = (c) => ({ x: (c.lng - lng0) * mlng, y: (c.lat - lat0) * mlat });
    const toLL = (x, y) => ({ lat: +(lat0 + y / mlat).toFixed(8), lng: +(lng0 + x / mlng).toFixed(8) });
    const falde = roof.falde || [];
    const faldaMeters = {}, excl = [], all = [];
    falde.forEach((f) => {
      const mp = (f.coordinates || []).map(toM);
      faldaMeters[f.id] = mp; mp.forEach((p) => all.push(p));
      (f.esclusioni || []).forEach((e) => { const ep = (e.coordinates || []).map(toM); if (ep.length >= 3) { excl.push(ep); ep.forEach((p) => all.push(p)); } });
    });
    const mod = fl.module || moduleInfo || roof.module || {};
    const pw = (mod.lunghezza_mm || 1962) / 1000; // lato lungo (larghezza fila)
    const ph = (mod.larghezza_mm || 1134) / 1000;  // lato corto proiettato ~ A
    const xs = all.map((p) => p.x), ys = all.map((p) => p.y);
    let minx = Math.min(...xs), maxx = Math.max(...xs), miny = Math.min(...ys), maxy = Math.max(...ys);
    const mx = (maxx - minx) * 0.18 + 1, my = (maxy - miny) * 0.18 + 1;
    minx -= mx; maxx += mx; miny -= my; maxy += my;
    const AR = 1.5; let cw = maxx - minx, ch = maxy - miny;
    if (cw / ch > AR) { const nh = cw / AR, d = (nh - ch) / 2; miny -= d; maxy += d; }
    else { const nw = ch * AR, d = (nw - cw) / 2; minx -= d; maxx += d; }
    const W = 960, H = Math.round(W / AR);
    const px = (p) => ({ x: ((p.x - minx) / (maxx - minx)) * W, y: ((maxy - p.y) / (maxy - miny)) * H });
    const mFromPx = (sx, sy) => ({ x: minx + (sx / W) * (maxx - minx), y: maxy - (sy / H) * (maxy - miny) });
    const sw = toLL(minx, miny), ne = toLL(maxx, maxy);
    const satUrl = `${API}/roof/satellite?bbox=${sw.lng},${sw.lat},${ne.lng},${ne.lat}&w=${W}&h=${H}`;
    const pdfImg = pdfBgImage(roof, toM, px);
    return { pdfImg, toM, toLL, faldaMeters, excl, falde, W, H, px, mFromPx, satUrl, pw, ph, spanX: maxx - minx };
  }, [roof, fl, moduleInfo]);

  const idRef = useRef(1);
  const derivePanels = useCallback((flx) => (flx.panels || []).map((pn) => {
    const mp = (pn.corners || []).map(geo.toM);
    const cx = mp.reduce((s, p) => s + p.x, 0) / (mp.length || 1);
    const cy = mp.reduce((s, p) => s + p.y, 0) / (mp.length || 1);
    let ang = 0, w = geo.pw, h = geo.ph;
    if (mp.length >= 3) {
      ang = (Math.atan2(mp[1].y - mp[0].y, mp[1].x - mp[0].x) * 180) / Math.PI;
      w = Math.hypot(mp[1].x - mp[0].x, mp[1].y - mp[0].y);
      h = Math.hypot(mp[2].x - mp[1].x, mp[2].y - mp[1].y);
    }
    return { id: idRef.current++, cx, cy, ang, w, h, row: pn.row };
  }), [geo]);
  const [panels, setPanels] = useState(() => derivePanels(fl));
  // In sola lettura, ri-deriva i pannelli quando il layout cambia (rotazione orientamento in tempo reale).
  useEffect(() => { if (!editable) setPanels(derivePanels(fl)); }, [fl, editable, derivePanels]);
  const [mode, setMode] = useState("move");
  const [saving, setSaving] = useState(false);
  const svgRef = useRef(null);
  const az = fl.params?.azimuth_deg ?? 180;
  const fieldAng = 90 - az; // per orientare i nuovi pannelli col campo

  const clientToM = (e) => {
    const p = svgPoint(svgRef.current, e);
    return p ? geo.mFromPx(p.x, p.y) : null;
  };
  const V = useEditorViewport(svgRef, geo.W, geo.H);
  const SO = useSatOffset(roof);
  const showSat = !geo.pdfImg && !roof?.draw_mode;
  const S = usePanelSelection({ panels, setPanels, clientToM, geo, editable, mode });
  const onPanelDown = S.onPanelDown;
  const onMove = S.onMove;
  const onUp = () => S.onUp();
  const onBgDown = (e) => {
    if (!editable || S.onBgDown(e)) return;
    const m = clientToM(e); if (!m) return;
    const nid = idRef.current++;
    setPanels((ps) => [...ps, { id: nid, cx: m.x, cy: m.y, ang: fieldAng, w: geo.pw, h: geo.ph, row: null }]);
    S.setSel(new Set([nid]));
  };
  const del = S.remove;
  const ST = usePanelString({ pw: geo.pw, ph: geo.ph, gap: 0.02, rowPitch: fl.geometry?.D_mm ? fl.geometry.D_mm / 1000 : null, angAt: () => fieldAng,
    onPlace: (blk) => {
      const ids = [];
      const add = blk.map((b) => { const nid = idRef.current++; ids.push(nid); return { id: nid, ...b, row: null }; });
      setPanels((ps) => [...ps, ...add]);
      S.setSel(new Set(ids));
    } });
  const rotate = (deg) => { if (S.sel.size) setPanels((ps) => rotateGroup(ps, S.sel, deg)); };

  const save = async () => {
    setSaving(true);
    try {
      const body = { panels: panels.map((p) => ({ row: p.row, corners: rectCornersFromCenter(p.cx, p.cy, p.w, p.h, p.ang).map((c) => geo.toLL(c.x, c.y)) })) };
      await SO.persist(base);
      const { data } = await api.put(`${base}/flat-roof-layout/panels`, body);
      toast.success(`Salvato: ${data.summary?.count} pannelli · ${data.summary?.kwp} kWp`);
      onSaved(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  // quota D tra le prime due file
  const dQuota = useMemo(() => {
    if (panels.length < 2) return null;
    const rows = {};
    panels.forEach((p) => { const k = p.row ?? 0; (rows[k] = rows[k] || []).push(p); });
    const keys = Object.keys(rows).map(Number).sort((a, b) => a - b);
    if (keys.length < 2) return null;
    const a = rows[keys[0]][0], b = rows[keys[1]][0];
    return { a: geo.px({ x: a.cx, y: a.cy }), b: geo.px({ x: b.cx, y: b.cy }), d: fl.geometry?.D_mm };
  }, [panels, geo, fl]);

  const P = (m) => geo.px(m);
  return (
    <div data-testid="flat-roof-svg" className={editable ? V.wrapClass : ""}>
      {editable && <StringDialog s={ST} prefix="flat" />}
      {editable && (
        <div className={`flex flex-wrap items-center gap-2 ${V.fs ? "shrink-0" : "mb-2"}`}>
          <ViewportToolbar v={V} prefix="flat" />
          <Button size="sm" variant={mode === "move" ? "default" : "outline"} onClick={() => setMode("move")} data-testid="flat-mode-move"><Move className="h-4 w-4 mr-1" strokeWidth={1.5} />Sposta</Button>
          <Button size="sm" variant={mode === "add" ? "default" : "outline"} onClick={() => setMode("add")} data-testid="flat-mode-add"><Plus className="h-4 w-4 mr-1" strokeWidth={1.5} />Aggiungi</Button>
          <Button size="sm" variant="outline" onClick={del} disabled={!S.sel.size} data-testid="flat-delete"><Trash2 className="h-4 w-4 mr-1" strokeWidth={1.5} />Elimina</Button>
          <StringButton s={ST} prefix="flat" />
          <GroupRotate disabled={!S.sel.size} onRotate={rotate} prefix="flat" />
          <SelectionToolbar s={S} prefix="flat" />
          {showSat && <SatAlignControls s={SO} prefix="flat" />}
          <span className="text-sm font-semibold ml-1" data-testid="flat-edit-count">{panels.length} pannelli</span>
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="ghost" onClick={onClose} data-testid="flat-edit-cancel"><X className="h-4 w-4 mr-1" />Annulla</Button>
            <Button size="sm" onClick={save} disabled={saving} data-testid="flat-edit-save">{saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" strokeWidth={1.5} />}Salva</Button>
          </div>
        </div>
      )}
      <div className={`w-full rounded-lg border border-border overflow-hidden relative ${editable && V.fs ? "flex-1 min-h-0" : ""}`} style={{ touchAction: "none" }}>
        <svg ref={svgRef} viewBox={editable ? V.viewBox : `0 0 ${geo.W} ${geo.H}`} className={editable ? V.svgClass : "w-full block select-none"} data-testid="flat-editor-svg"
          onPointerDownCapture={(e) => { if (editable && ST.active && e.button === 0 && !e.altKey) { e.stopPropagation(); ST.place(); return; } if (editable) V.onDown(e); }}
          onPointerMove={(e) => { if (editable && ST.active) ST.move(clientToM(e)); if (!(editable && V.onMove(e))) onMove(e); }} onPointerUp={(e) => { if (!(editable && V.onUp(e))) onUp(); }} onPointerLeave={(e) => { if (!(editable && V.onUp(e))) onUp(); }}>
          {geo.pdfImg ? <><rect x="0" y="0" width={geo.W} height={geo.H} fill="#fff" /><image href={geo.pdfImg.href} x={geo.pdfImg.x} y={geo.pdfImg.y} width={geo.pdfImg.width} height={geo.pdfImg.height} preserveAspectRatio="none" data-testid="editor-pdf-bg" /></> : roof?.draw_mode ? <EditorGridBg geo={geo} /> : <><rect x="0" y="0" width={geo.W} height={geo.H} fill="#1f2a1f" /><SatImage geo={geo} off={SO.off} /></>}
          <rect x="0" y="0" width={geo.W} height={geo.H} fill="transparent" onPointerDown={onBgDown} style={{ cursor: editable && mode === "add" ? "copy" : editable ? "crosshair" : "default" }} data-testid="flat-editor-bg" />
          {geo.falde.map((f) => {
            const pts = (geo.faldaMeters[f.id] || []).map((m) => { const p = P(m); return `${p.x.toFixed(1)},${p.y.toFixed(1)}`; }).join(" ");
            return <polygon key={f.id} points={pts} fill="none" stroke="#FDD003" strokeWidth="2.5" pointerEvents="none" />;
          })}
          {geo.excl.map((ex, i) => {
            const pts = ex.map((m) => { const p = P(m); return `${p.x.toFixed(1)},${p.y.toFixed(1)}`; }).join(" ");
            return <polygon key={`e${i}`} points={pts} fill="#DC262688" stroke="#DC2626" strokeWidth="2" pointerEvents="none" />;
          })}
          {panels.map((p) => {
            const corners = rectCornersFromCenter(p.cx, p.cy, p.w, p.h, p.ang);
            if (corners.some((c) => !isFinite(c.x))) return null;
            const pts = corners.map((m) => { const q = P(m); return `${q.x.toFixed(1)},${q.y.toFixed(1)}`; }).join(" ");
            const on = S.sel.has(p.id);
            return <polygon key={p.id} points={pts} fill={on ? "#FDD003CC" : "#1E3A8ACC"} stroke={on ? "#B45309" : "#0B1B4D"} strokeWidth={on ? 2.5 : 1} data-testid={`flat-panel-${p.id}`} data-selected={on ? "1" : "0"}
              style={{ cursor: editable ? (mode === "move" ? "grab" : "pointer") : "default" }} onPointerDown={(e) => onPanelDown(e, p)} />;
          })}
          <MarqueeRect box={S.box} />
          <StringPreview s={ST} P={P} />
          {/* quota D tra prime due file */}
          {dQuota && (
            <g pointerEvents="none">
              <line x1={dQuota.a.x} y1={dQuota.a.y} x2={dQuota.b.x} y2={dQuota.b.y} stroke="#ffffff" strokeWidth="2" strokeDasharray="6 4" />
              <rect x={(dQuota.a.x + dQuota.b.x) / 2 - 60} y={(dQuota.a.y + dQuota.b.y) / 2 - 12} width="120" height="20" rx="4" fill="#0B1B4D" />
              <text x={(dQuota.a.x + dQuota.b.x) / 2} y={(dQuota.a.y + dQuota.b.y) / 2 + 3} textAnchor="middle" fontSize="13" fontWeight="700" fill="#fff">D = {num(dQuota.d)} mm</text>
            </g>
          )}
          {/* Nord + azimut campo */}
          <g pointerEvents="none" transform={`translate(${geo.W - 54} 48)`}>
            <circle cx="0" cy="0" r="26" fill="#00000088" />
            <line x1="0" y1="16" x2="0" y2="-16" stroke="#fff" strokeWidth="2" />
            <polygon points="0,-22 -5,-12 5,-12" fill="#ef4444" />
            <text x="0" y="-28" textAnchor="middle" fontSize="12" fontWeight="700" fill="#fff">N</text>
            <line x1="0" y1="0" x2={16 * Math.sin((az * Math.PI) / 180)} y2={-16 * Math.cos((az * Math.PI) / 180)} stroke="#FDD003" strokeWidth="2.5" />
          </g>
        </svg>
      </div>
      <p className="text-[11px] text-muted-foreground mt-1.5 flex items-center gap-1"><Compass className="h-3.5 w-3.5" /> Freccia gialla = orientamento pannelli (azimut {Math.round(az)}°) · quota bianca tratteggiata = distanza libera D tra le file.</p>
    </div>
  );
}
